import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/api/client';
import { useDealOptions, useRegOptions } from '@/api/m3';
import { Button } from '@/components/kit/Button';
import { Option } from '@/components/kit/Controls';
import { NumberField, TextField } from '@/components/kit/Field';
import { Breadcrumbs, Columns, Kv, PageHeader, SectionLabel } from '@/components/kit/Layout';
import { ErrorState, Note, Skeleton } from '@/components/kit/Note';
import { FieldHint, OptionList } from '@/components/kit/OptionList';
import { PopoverSelect } from '@/components/kit/Popover';
import { money, date as ruDate, withPlural } from '@/lib/format';
import { years } from '@/lib/phone';
import { CityField, type CityValue } from '@/features/base/shared/CityField';
import { stageTitle } from '@/features/crm/labels';
import { useM3Submit } from '@/features/crm/useM3Submit';

const NEW_TEAM = 'new';

/**
 * Новая регистрация (viewNewReg в макете, БЛ 5.12): сделка задаёт
 * клиента и мероприятие; номинация — из мероприятия сделки, с ценой
 * и местами; команда клиента или новая по минимуму; число пустых строк.
 */
export function NewRegistrationPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [dealId, setDealId] = useState<string | null>(params.get('dealId'));
  const deals = useDealOptions();
  const options = useRegOptions(dealId);
  const [nominationId, setNominationId] = useState<string | null>(null);
  const [team, setTeam] = useState<string | null>(null);
  const [teamName, setTeamName] = useState('');
  const [teamCity, setTeamCity] = useState<CityValue | null>(null);
  // По умолчанию 0: состав, которого менеджер не знает, не выдумываем и счёт на него не выставляем
  const [count, setCount] = useState<number | null>(0);

  const o = options.data;
  const nom = o?.nominations.find((n) => n.id === nominationId) ?? null;

  // Сделка выбрана — первая команда клиента; команд нет — «Новая команда»
  useEffect(() => {
    if (!o) return;
    setNominationId(null);
    setTeam(o.teams[0]?.id ?? NEW_TEAM);
    setTeamCity(null);
  }, [o?.deal.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const participants = nom?.isSolo ? 1 : (count ?? 0);
  const price = nom?.defaultPackage?.price ?? 0;
  const { submit, pending } = useM3Submit(
    () =>
      api.post<{ id: string; status: string }>('/registrations', {
        dealId,
        nominationId,
        ...(nom?.isSolo
          ? {}
          : team === NEW_TEAM
            ? { newTeam: { name: teamName.trim(), cityId: teamCity?.id } }
            : { teamId: team }),
        participants,
      }),
    (r) =>
      r.status === 'reserve'
        ? 'Номинация закрыта — регистрация встала в резерв, счёт не выставлен'
        : participants
          ? `Регистрация создана: ${withPlural(participants, 'строка', 'строки', 'строк')}, счёт ${money(price * participants)}`
          : 'Регистрация создана без участников — добавьте их в карточке номера',
    (r) => navigate(`/registrations/${r.id}`),
  );

  const ready =
    Boolean(o && nom && !o.deal.lost) &&
    (nom!.isSolo || (team && (team !== NEW_TEAM || (teamName.trim() && teamCity))));
  const back = () => navigate(dealId && params.get('dealId') ? `/crm/deals/${dealId}` : '/registrations');

  return (
    <div className="main">
      <Breadcrumbs
        items={[
          o && params.get('dealId')
            ? { label: `Сделка · ${o.deal.client.shortName}`, onClick: () => navigate(`/crm/deals/${o.deal.id}`) }
            : { label: 'Регистрации', onClick: () => navigate('/registrations') },
          { label: 'Новая регистрация' },
        ]}
      />
      <PageHeader title="Новая регистрация" />
      <Columns form>
        <div className="form formcol">
          <SectionLabel>Сделка</SectionLabel>
          <div className="field">
            <PopoverSelect
              asField
              value={dealId}
              onChange={setDealId}
              placeholder="Выберите сделку"
              options={(deals.data ?? []).map((d) => ({
                value: d.id,
                label: `${d.client} · ${d.event.label}`,
                side: stageTitle(d.stage),
                group: d.event.label,
              }))}
            />
            <FieldHint>
              Мероприятие берётся из сделки и в регистрации не меняется. Нет сделки — сначала квалифицируйте заявку или
              создайте сделку в CRM.
            </FieldHint>
          </div>
          {options.error ? <ErrorState message={options.error.message} onRetry={() => void options.refetch()} /> : null}
          {dealId && options.isLoading ? <Skeleton width="60%" /> : null}
          {o?.deal.lost ? <Note tone="warn">Сделка проиграна — сначала верните её в работу, потом регистрируйте.</Note> : null}

          {o ? (
            <>
              <SectionLabel>Номинация · {o.deal.event.label}</SectionLabel>
              <OptionList
                value={nominationId}
                onChange={setNominationId}
                options={o.nominations.map((n) => ({
                  value: n.id,
                  title: `${n.label}${n.isSolo ? ' · сольная' : ''}`,
                  description: (
                    <>
                      {n.defaultPackage ? `${n.defaultPackage.name} ${money(n.defaultPackage.price)} · ` : ''}
                      оплатили {n.paidSeats} из {n.limit} мест
                      {n.unpaid ? ` · без оплаты ${n.unpaid}` : ''}
                      {n.willReserve ? <span className="wtx"> · номинация закрыта — встанет в резерв</span> : null}
                    </>
                  ),
                }))}
              />
            </>
          ) : null}

          {o && nom?.isSolo ? (
            <>
              <SectionLabel>Солист</SectionLabel>
              <div className="olist">
                <Option
                  radio
                  checked
                  onToggle={() => undefined}
                  title={o.soloist.fullName}
                  description={`клиент сделки${o.soloist.ageAtEvent !== null ? ` · ${years(o.soloist.ageAtEvent)} на дату выступления` : ''}`}
                />
              </div>
              <Note>Соло — отдельная сделка солиста. Другой солист — через его собственную сделку.</Note>
            </>
          ) : null}

          {o && nom && !nom.isSolo ? (
            <>
              <SectionLabel>Команда</SectionLabel>
              <OptionList
                value={team}
                onChange={setTeam}
                options={[
                  ...o.teams.map((t) => ({
                    value: t.id,
                    title: t.name,
                    description: `${t.city} · в составе ${t.members}${t.awaitingHead ? ' · ждём руководителя' : ''}`,
                  })),
                  { value: NEW_TEAM, title: 'Новая команда', description: 'заводится по минимуму, руководитель уточнит позже' },
                ]}
              />
              {team === NEW_TEAM ? (
                <>
                  <div className="row">
                    <div className="col">
                      <TextField label="Название команды" placeholder="Например, Пульс Kids" value={teamName} onValue={setTeamName} />
                    </div>
                    <div className="col">
                      <CityField label="Город команды" value={teamCity} onChange={setTeamCity} />
                    </div>
                  </div>
                  <Note>Команда заведётся с отметкой «ждём руководителя». Это ничего не блокирует.</Note>
                </>
              ) : null}
              <SectionLabel>Участники</SectionLabel>
              <div className="row">
                <div className="col fix">
                  <NumberField label="Число участников" size={6} value={count} onValue={setCount} />
                </div>
              </div>
              {participants ? (
                <Note>
                  Создастся {withPlural(participants, 'пустая строка', 'пустые строки', 'пустых строк')} с пакетом «
                  {nom.defaultPackage?.name ?? '—'}».
                  {team !== NEW_TEAM ? ' Строки можно сразу заменить людьми из состава команды.' : ''} Пакеты отдельным
                  участникам менеджер меняет в карточке номера.
                </Note>
              ) : (
                <Note>
                  Номер заведётся без участников, счёт — 0. Участников добавите в карточке номера: из состава команды
                  или вручную.
                </Note>
              )}
            </>
          ) : null}

          <div className="fbtns">
            <Button tone="primary" onClick={submit} disabled={!ready || pending}>
              Создать регистрацию
            </Button>
            <Button onClick={back}>Отмена</Button>
          </div>
        </div>
        <div>
          <div className="blk">
            <SectionLabel>Итог</SectionLabel>
            <Kv k="Мероприятие">{o ? o.deal.event.label : <span className="na">—</span>}</Kv>
            <Kv k="Номинация">{nom ? nom.label : <span className="dim">не выбрана</span>}</Kv>
            <Kv k="Цена за участника">
              {nom?.defaultPackage ? (
                <>
                  {money(price)} <span className="muted">· {nom.defaultPackage.period === 1 ? 'первая цена' : 'вторая цена'}</span>
                </>
              ) : (
                <span className="na">—</span>
              )}
            </Kv>
            <Kv k="Участников">{nom ? participants : <span className="na">—</span>}</Kv>
            <Kv k="Счёт">
              {nom ? nom.willReserve ? <span className="wtx">не выставится — резерв</span> : money(price * participants) : <span className="na">—</span>}
            </Kv>
            {nom?.willReserve ? (
              <Note tone="warn">
                Номинация закрыта: регистрация встанет в резерв без счёта. Поднять из резерва можно в карточке номера.
              </Note>
            ) : nom ? (
              <Note>
                Место закрепит первый платёж — он же зафиксирует цену.
                {nom.defaultPackage?.period === 1 && nom.defaultPackage.raiseOn ? ` Повышение ${ruDate(nom.defaultPackage.raiseOn)}.` : ''}
              </Note>
            ) : null}
          </div>
        </div>
      </Columns>
    </div>
  );
}
